import { useEffect, useState } from 'react'

import { api, ApiError, can, refusalReason, type VendorRequestPolicy } from '../../lib/api'
import { ErrorBox, Loading } from '../../components/DataTable'
import { Drawer, Status } from '../../components/kit'
import { useToast } from '../../components/ui'

// Who approves a vendor request (release 3.3, the request step before due
// diligence).
//
// # WHY THIS EXISTS
//
// A vendor is Requested until a TPRM lead approves the request, and no
// questionnaire reaches it before then. By default a lead may approve a request
// they raised, in the same click as adding the vendor (the founder's decision
// of 12 Oct). A tenant that wants a second pair of eyes at the request too turns
// this on, and from then on the person who raised a request neither approves
// nor rejects it. The setting has no other home, so its editor opens over the
// Workflows page, as the finding deadlines do (?edit=vendor-requests).
//
// Changing it is the programme's own rule, programme.write; the server holds
// the rule (domain.CheckRequestDecision) and this only draws and saves it.

export function VendorRequests({ open, onClose, onSaved }: { open: boolean; onClose: () => void; onSaved: () => void }) {
  const toast = useToast()
  const [saved, setSaved] = useState<VendorRequestPolicy | null>(null)
  const [draft, setDraft] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)
  const [refused, setRefused] = useState('')

  useEffect(() => {
    if (!open) return
    setRefused('')
    api
      .vendorRequestPolicy()
      .then((p) => {
        setSaved(p)
        setDraft(p.secondPersonApproves)
        setError(null)
      })
      .catch(setError)
  }, [open])

  const mayWrite = can('programme.write')
  const dirty = saved !== null && draft !== saved.secondPersonApproves

  async function save() {
    setBusy(true)
    setRefused('')
    try {
      const p = await api.setVendorRequestPolicy(draft)
      setSaved(p)
      toast(p.secondPersonApproves ? 'A second person now approves every vendor request.' : 'A TPRM lead may now approve a request they raised.')
      onSaved()
      onClose()
    } catch (err) {
      setRefused(err instanceof ApiError ? (err.issues[0]?.detail ?? err.message) : String(err))
    } finally {
      setBusy(false)
    }
  }

  const footer = mayWrite ? (
    <button type="button" className="button primary" disabled={busy || !dirty} onClick={() => void save()} data-action="tprm.settings-workflows.a02">
      {busy ? 'Saving' : 'Save'}
    </button>
  ) : (
    <span className="sw-row">
      <Status tone="neutral">Read only</Status>
      <span className="field-note">{refusalReason('programme.write')}</span>
    </span>
  )

  return (
    <Drawer open={open} onClose={onClose} title="Who approves a vendor request" footer={footer}>
      {error != null ? (
        <ErrorBox error={error} what="The setting" />
      ) : !saved ? (
        <Loading what="the setting" />
      ) : (
        <div className="wf-deadlines">
          <p className="obs-what">A TPRM lead approves a vendor request before any questionnaire is sent.</p>
          <label className="sw-row">
            <input type="checkbox" checked={draft} disabled={!mayWrite} onChange={(e) => setDraft(e.target.checked)} />
            <span>A second person approves a vendor request</span>
          </label>
          <p className="field-note">
            Off: a lead may approve a request they raised, in one click. On: somebody other than the person who raised a request approves or rejects it.
          </p>
          {refused && (
            <p className="field-error" role="alert">
              {refused}
            </p>
          )}
        </div>
      )}
    </Drawer>
  )
}
